import { useState, useEffect, useCallback } from 'react';
import { KeyRound, RefreshCw, ShieldAlert, Users } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const LegacyAuthAudit = () => {
  const [audit, setAudit] = useState([]);
  const [flagged, setFlagged] = useState([]);
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(200);

  const fetchAudit = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const res = await api.get(`/admin/legacy-auth/audit?limit=${limit}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAudit(res.data?.audit || []);
      setFlagged(res.data?.flagged_users || []);
    } catch (error) {
      console.error('Legacy audit error:', error);
      toast.error('Failed to load legacy auth audit');
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchAudit();
  }, [fetchAudit]);

  const fmt = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString();
    } catch (_e) {
      return iso;
    }
  };

  const shortUA = (ua) => (ua && ua.length > 60 ? `${ua.slice(0, 60)}…` : ua || '—');

  return (
    <div className="space-y-6" data-testid="legacy-audit-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
            <KeyRound className="h-6 w-6 text-amber-400" /> Legacy Auth Audit
          </h1>
          <p className="text-gray-400 text-sm">Every legacy-bypass login attempt and every account currently flagged.</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={limit}
            onChange={(e) => setLimit(parseInt(e.target.value, 10))}
            className="px-3 py-2 bg-gray-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
            data-testid="legacy-audit-limit"
          >
            <option value={50}>Last 50</option>
            <option value={200}>Last 200</option>
            <option value={500}>Last 500</option>
            <option value={1000}>Last 1000</option>
          </select>
          <button
            onClick={fetchAudit}
            className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 rounded-lg text-black font-semibold text-sm"
            data-testid="legacy-audit-refresh"
          >
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-amber-500/10 border border-amber-500/40 rounded-xl p-5 flex items-start gap-4">
          <div className="p-3 rounded-lg bg-amber-500/20"><ShieldAlert className="h-6 w-6 text-amber-300" /></div>
          <div>
            <p className="text-amber-200 text-xs">Flagged accounts</p>
            <p className="text-white text-3xl font-bold">{flagged.length}</p>
            <p className="text-amber-100/70 text-xs mt-1">Users allowed to log in with any password.</p>
          </div>
        </div>
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-5 flex items-start gap-4">
          <div className="p-3 rounded-lg bg-gray-700"><Users className="h-6 w-6 text-gray-200" /></div>
          <div>
            <p className="text-gray-400 text-xs">Recent legacy logins</p>
            <p className="text-white text-3xl font-bold">{audit.length}</p>
            <p className="text-gray-400 text-xs mt-1">Sorted by most recent attempt.</p>
          </div>
        </div>
      </div>

      {/* Flagged users */}
      <div className="bg-gray-900 rounded-xl border border-gray-800">
        <div className="p-4 border-b border-gray-800">
          <h3 className="text-white font-bold">Currently flagged users</h3>
        </div>
        {flagged.length === 0 ? (
          <div className="p-6 text-gray-400 text-sm">No users have legacy auth enabled.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-800 text-gray-400">
                <tr>
                  <th className="text-left px-4 py-2">Email</th>
                  <th className="text-left px-4 py-2">Name</th>
                  <th className="text-left px-4 py-2">Enabled at</th>
                  <th className="text-left px-4 py-2">Enabled by</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {flagged.map((u) => (
                  <tr key={u.user_id} className="hover:bg-gray-800/50">
                    <td className="px-4 py-2 text-white">{u.email || '—'}</td>
                    <td className="px-4 py-2 text-gray-300">{u.name || '—'}</td>
                    <td className="px-4 py-2 text-gray-300">{fmt(u.legacy_auth_updated_at)}</td>
                    <td className="px-4 py-2 text-gray-300 font-mono text-xs">{u.legacy_auth_updated_by || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Audit entries */}
      <div className="bg-gray-900 rounded-xl border border-gray-800">
        <div className="p-4 border-b border-gray-800">
          <h3 className="text-white font-bold">Recent bypass logins</h3>
        </div>
        {loading ? (
          <div className="p-8 text-gray-400 text-sm">Loading…</div>
        ) : audit.length === 0 ? (
          <div className="p-6 text-gray-400 text-sm">No legacy logins recorded yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-800 text-gray-400">
                <tr>
                  <th className="text-left px-4 py-2">When</th>
                  <th className="text-left px-4 py-2">Email</th>
                  <th className="text-left px-4 py-2">IP</th>
                  <th className="text-left px-4 py-2">User agent</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {audit.map((e) => (
                  <tr key={e.id} className="hover:bg-gray-800/50">
                    <td className="px-4 py-2 text-gray-200 whitespace-nowrap">{fmt(e.logged_in_at)}</td>
                    <td className="px-4 py-2 text-white">{e.email || '—'}</td>
                    <td className="px-4 py-2 text-gray-300 font-mono text-xs">{e.ip_address}</td>
                    <td className="px-4 py-2 text-gray-400 text-xs">{shortUA(e.user_agent)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default LegacyAuthAudit;
